import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu as MenuIcon, X, MapPin } from "lucide-react";

const links = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/menu", label: "Menu", testid: "nav-menu-link" },
  { to: "/catering", label: "Catering", testid: "nav-catering-link" },
];

const linkClass = ({ isActive }) =>
  `relative font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:text-rust ${
    isActive ? "text-rust" : "text-smoke"
  }`;

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-sand bg-cream/90 backdrop-blur-xl">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo-link" className="group flex items-center gap-3">
          <img
            src="/logo.png"
            alt="Babylon Deli logo"
            className="h-12 w-12 rounded-full shadow-sm transition-transform duration-500 group-hover:rotate-[352deg]"
          />
          <span className="leading-none">
            <span className="block font-display text-xl font-bold tracking-tight text-ink">Babylon Deli</span>
            <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.3em] text-rust">Mount Druitt · NSW</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-10 md:flex" data-testid="nav-desktop">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={l.testid} className={linkClass}>
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/#find-us"
            data-testid="nav-visit-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-espresso px-5 py-2.5 font-mono text-xs uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-rust"
          >
            <MapPin className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5" />
            Visit Us
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          className="grid h-11 w-11 place-items-center rounded-full border border-sand text-ink transition-colors duration-300 hover:border-rust hover:text-rust md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-sand bg-cream md:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-6">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-lg px-4 py-3 font-display text-2xl font-bold transition-colors duration-300 ${
                      isActive ? "bg-paper text-rust" : "text-ink hover:bg-paper"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/#find-us"
                data-testid="nav-visit-cta-mobile"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-espresso px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] text-cream"
              >
                <MapPin className="h-3.5 w-3.5" />
                Visit Us
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
